<template>
  <!--
    CPA 退出挽留弹窗
    对齐 public/func/productList.html openReturnPop + 小程序 quit-retain-modal
    用法：传入 quitProductId，组件拦截浏览器返回并拉 h5cpa/quit 展示
  -->
  <van-dialog
    v-model="visible"
    :close-on-click-overlay="false"
    :show-confirm-button="false"
    class="quit-retain-modal"
  >
    <div class="quit-retain-modal__body">
      <div class="quit-retain-modal__head">
        <span class="quit-retain-modal__title">申请预审已通过</span>
        <img
          class="quit-retain-modal__close"
          src="../../../public/img/productList/icon_colse.png"
          alt=""
          @click="close"
        />
      </div>

      <template v-if="!loading">
        <div class="quit-retain-modal__product">
          <img
            class="quit-retain-modal__logo"
            :src="productView.productLogo"
            alt=""
          />
          <div>
            <span class="quit-retain-modal__name">{{
              productView.productName
            }}</span>
            <span class="quit-retain-modal__tags">{{
              productView.productTagTxt
            }}</span>
          </div>
        </div>
        <div class="quit-retain-modal__line"></div>
        <div class="quit-retain-modal__row">
          <span class="quit-retain-modal__label">最高可借(元)</span>
          <span class="quit-retain-modal__value">{{
            productView.creditAmount
          }}</span>
        </div>
        <div class="quit-retain-modal__row">
          <span class="quit-retain-modal__label">最低年化利率</span>
          <span class="quit-retain-modal__value"
            >{{ productView.timeLimitLoanRate }}%</span
          >
        </div>
        <div class="quit-retain-modal__row">
          <span class="quit-retain-modal__label">申请人数</span>
          <span class="quit-retain-modal__value">{{
            productView.loanNumber
          }}</span>
        </div>
        <div class="quit-retain-modal__footer">
          <span class="quit-retain-modal__btn" @click="onApply">立即申请</span>
          <span
            v-if="showCancel"
            class="quit-retain-modal__cancel"
            @click="onCancel"
            >取消</span
          >
        </div>
      </template>

      <div v-else class="quit-retain-modal__loading">加载中...</div>
    </div>
  </van-dialog>
</template>

<script>
/**
 * CPA 退出挽留弹窗
 *
 * - 注册 popstate 拦截浏览器返回
 * - GET /product/api/h5cpa/quit/{quitProductId} 拉随机推荐产品
 * - 立即申请：emit apply(完整产品对象)，由父页走撞库 / 跳转
 * - 取消（showCancel）：emit cancel，由父页决定跳转（如贷超列表）
 */
import * as API_Common from "@/api/common.js";

const EMPTY_VIEW = {
  productName: "",
  productLogo: "",
  creditAmount: "",
  loanNumber: "",
  timeLimitLoanRate: "",
  productTagTxt: "",
};

/** tagList → 展示文案 */
function buildProductTagText(tagList) {
  return (tagList || []).filter(Boolean).join(" ");
}

/** 接口 data → 弹窗展示字段 */
function mapProductView(data) {
  return {
    productName: data.productName || "",
    productLogo: data.imgUrl || "",
    creditAmount: data.creditAmount || "",
    loanNumber: data.loanNumber || "",
    timeLimitLoanRate: data.timeLimitLoanRate || "",
    productTagTxt: buildProductTagText(data.tagList),
  };
}

export default {
  name: "QuitRetainModal",
  props: {
    /** 挽留接口入参：当前页推荐产品 ID */
    quitProductId: {
      type: [Number, String],
      default: null,
    },
    /** true 时不再拦截返回（父页已确认跳转三方） */
    allowLeave: {
      type: Boolean,
      default: false,
    },
    /** 是否启用返回拦截，默认开启 */
    interceptBack: {
      type: Boolean,
      default: true,
    },
    /** 是否在立即申请下方展示「取消」文字按钮 */
    showCancel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: false,
      loading: false,
      quitProduct: null, // h5cpa/quit 完整产品
      productView: { ...EMPTY_VIEW },
      backBound: false,
    };
  },
  watch: {
    visible(val) {
      // 弹窗打开时补 history，再点返回先关弹窗
      if (val) {
        window.history.pushState(null, null, document.URL);
      }
    },
    interceptBack(val) {
      if (!val) {
        this.unbindBack();
      }
    },
    /**
     * 推荐产品加载完成后再注册返回拦截
     * 避免 bindBack 过早执行导致 history 栈与 Vue Router 不同步
     */
    quitProductId(val) {
      if (val && this.interceptBack) {
        this.bindBack();
      }
    },
  },
  mounted() {
    if (this.quitProductId && this.interceptBack) {
      this.bindBack();
    }
  },
  beforeDestroy() {
    this.unbindBack();
  },
  methods: {
    /** pushState + popstate，拦截浏览器返回 */
    bindBack() {
      if (this.backBound || !this.interceptBack) {
        return;
      }
      window.history.pushState(null, null, document.URL);
      window.addEventListener("popstate", this.onPopState, false);
      this.backBound = true;
    },

    unbindBack() {
      if (!this.backBound) {
        return;
      }
      window.removeEventListener("popstate", this.onPopState, false);
      this.backBound = false;
    },

    /** popstate 回调（对齐 registerEntry_tl12：处理完立即 pushState 防止路由离开） */
    async onPopState() {
      if (this.allowLeave) {
        return;
      }

      // 安卓 WebView 需额外调桥，否则 popstate 可能不触发
      if (this.client === "android") {
        try {
          window.tbs_bridge.nativeExec("network", "type", 0, null);
        } catch (e) {
          console.log(e);
        }
      }

      if (this.visible) {
        this.close();
        window.history.pushState(null, null, document.URL);
        return;
      }

      // 关键：立即压回 history，避免 Vue Router history 模式随 popstate 直接返回上一页
      window.history.pushState(null, null, document.URL);

      await this.fetchAndOpen();
    },

    /**
     * 拉取挽留产品并展示弹窗
     * GET /product/api/h5cpa/quit/{quitProductId}
     */
    async fetchAndOpen() {
      if (!this.quitProductId) {
        return;
      }

      this.visible = true;
      this.loading = true;
      this.quitProduct = null;
      this.productView = { ...EMPTY_VIEW };

      try {
        const res = await API_Common.h5CpaQuitRandom(this.quitProductId);
        if (res?.code === 200 && res.data) {
          this.quitProduct = res.data;
          this.productView = mapProductView(res.data);
          return;
        }
        this.visible = false;
      } catch (error) {
        console.error("[QuitRetainModal] fetchAndOpen failed", error);
        this.visible = false;
      } finally {
        this.loading = false;
      }
    },

    /** 关闭弹窗，留在当前页 */
    close() {
      this.visible = false;
      this.$emit("close");
    },

    /** 立即申请：把完整产品对象交给父页处理跳转 */
    onApply() {
      if (!this.quitProduct?.productId) {
        return;
      }
      this.close();
      this.$emit("apply", this.quitProduct);
    },

    /** 取消：关闭弹窗并交给父页（如跳转贷超） */
    onCancel() {
      this.close();
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .quit-retain-modal {
  width: 6.2rem;
  border-radius: 0.24rem;
  overflow: hidden;
}

.quit-retain-modal__body {
  padding: 0.4rem 0.36rem 0.36rem;
}

.quit-retain-modal__head {
  position: relative;
  margin-bottom: 0.32rem;
  text-align: center;
}

.quit-retain-modal__title {
  font-size: 0.36rem;
  font-weight: 500;
  color: #202020;
}

.quit-retain-modal__close {
  position: absolute;
  top: 50%;
  right: 0;
  width: 0.32rem;
  height: 0.32rem;
  transform: translateY(-50%);
  cursor: pointer;
}

.quit-retain-modal__product {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.24rem;
}

.quit-retain-modal__logo {
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 0.12rem;
  object-fit: cover;
  flex-shrink: 0;
}

.quit-retain-modal__name {
  display: block;
  font-size: 0.32rem;
  font-weight: 500;
  color: #202020;
  margin-bottom: 0.08rem;
}

.quit-retain-modal__tags {
  display: block;
  font-size: 0.24rem;
  color: #999;
}

.quit-retain-modal__line {
  height: 0.01rem;
  background: #eee;
  margin-bottom: 0.16rem;
}

.quit-retain-modal__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.16rem 0;
}

.quit-retain-modal__label {
  font-size: 0.28rem;
  color: #666;
}

.quit-retain-modal__value {
  font-size: 0.28rem;
  color: #202020;
  font-weight: 500;
}

.quit-retain-modal__footer {
  margin-top: 0.24rem;
  text-align: center;
}

.quit-retain-modal__btn {
  display: block;
  width: 100%;
  height: 0.88rem;
  line-height: 0.88rem;
  border-radius: 0.44rem;
  background: #678cf9;
  color: #fff;
  font-size: 0.32rem;
  text-align: center;
  cursor: pointer;
}

.quit-retain-modal__cancel {
  display: block;
  margin-top: 0.24rem;
  font-size: 0.28rem;
  color: #999;
  line-height: 0.4rem;
  text-align: center;
  cursor: pointer;
}

.quit-retain-modal__loading {
  padding: 0.8rem 0;
  text-align: center;
  font-size: 0.28rem;
  color: #999;
}
</style>
